import React, { useEffect, useState, useMemo } from 'react';
import { Card, Button, Image, Col, Row, Carousel } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet'; // Import Helmet for SEO
import { COLORS } from '../constants/colors';

function Product({ product }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [timeColor, setTimeColor] = useState('blue');
  const defaultImage = 'https://pigeonauction.co.za/images/';

  // Define dates safely
  const currentDate = new Date();
  const startDate = product?.startDate ? new Date(product.startDate) : null;
  const endDate = useMemo(() => {
    return product.endDate ? new Date(product.endDate) : null;
  }, [product.endDate]);
  const formattedEndDate = endDate ? endDate.toLocaleDateString() : 'N/A';
  const formattedStartedDate = startDate ? startDate.toLocaleDateString() : 'N/A';

  useEffect(() => {
    if (!product || !endDate) return;

    const countdownInterval = setInterval(() => {
      const currentTime = new Date();
      const difference = endDate - currentTime;

      if (difference <= 0) {
        clearInterval(countdownInterval);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });

        if (difference < 5 * 60 * 1000) {
          setTimeColor('red');
        } else if (difference < 24 * 60 * 60 * 1000) {
          setTimeColor('green');
        } else {
          setTimeColor('blue');
        }
      }
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [endDate, product]);

  // If no product, show the fallback card
  if (!product) {
    return (
      <Card className="p-4 my-3 shadow border-0 h-100">
        <Card.Body className="d-flex flex-column text-center">
          <Card.Title>No Product Available</Card.Title>
          <p>There are no active auctions at the moment.</p>
        </Card.Body>
      </Card>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{`${product.name} - Pigeon Auction`}</title>
        <meta name="description" content={`Bid on ${product.name} starting at R${product.price}. Ends on ${formattedEndDate}.`} />
        <meta property="og:title" content={`${product.name} - Pigeon Auction`} />
        <meta property="og:description" content={`Bid on ${product.name} starting at R${product.price}. Ends on ${formattedEndDate}.`} />
        <meta property="og:url" content={`https://pigeonauction.co.za/product/${product.slug}-p-${product._id}`} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={product.images.length > 0 ? product.images[0].image : defaultImage} />
      </Helmet>

      <Card className="p-4 my-3 shadow border-0 h-100">
        <Link to={`/product/${product.slug}-p-${product._id}`}>
          {product.images.length === 1 ? (
            <Image className="d-block w-100" src={product.images[0].image} alt="Product Image" height={200} fluid />
          ) : (
            <Carousel className="carousel-custom" variant="dark" interval={null}>
              {product.images.map((image) => (
                <Carousel.Item key={image.id}>
                  <Image className="d-block w-100" src={image.image} alt="Product Image" height={200} fluid />
                </Carousel.Item>
              ))}
            </Carousel>
          )}
        </Link>
        <Card.Body className="d-flex flex-column">
          <Link className="link-dark text-decoration-none border-bottom pb-2" to={`/product/${product.slug}-p-${product._id}`}>
            <Card.Title>{product.name.substring(0, 55)}{product.name.length > 55 ? '...' : ''}</Card.Title>
          </Link>

          <Row className="d-flex align-items-center my-1">
            {endDate > currentDate ? (
              startDate > currentDate ? (
                <Col className="p-1 text-nowrap">Start bid: R{product.price}</Col>
              ) : (
                <Col className="p-1 text-nowrap">Max bid: R{product.currentHighestBid}</Col>
              )
            ) : (
              <Col className="p-1 text-nowrap">Sold for: R{product.currentHighestBid}</Col>
            )}
          </Row>

          <Row className="d-flex align-items-center my-1">
            {endDate < currentDate ? (
              <Col className="p-1 card-text text-nowrap">Ended: {formattedEndDate}</Col>
            ) : startDate < currentDate ? (
              <Col className="p-1 card-text text-nowrap">Ends: {formattedEndDate}</Col>
            ) : (
              <Col className="p-1 card-text text-nowrap">Starts: {formattedStartedDate}</Col>
            )}
          </Row>

          {endDate > currentDate && startDate < currentDate && (
            <Row className="d-flex justify-content-center">
              <Col xs="auto">
                <div className="countdown-blocks-container product-countdown">
                  <div className={`countdown-block ${timeColor}`}>
                    <div className="countdown-item">{timeLeft.days}d</div>
                  </div>
                  <div className={`countdown-block ${timeColor}`}>
                    <div className="countdown-item">{timeLeft.hours}h</div>
                  </div>
                  <div className={`countdown-block ${timeColor}`}>
                    <div className="countdown-item">{timeLeft.minutes}m</div>
                  </div>
                  <div className={`countdown-block ${timeColor}`}>
                    <div className="countdown-item">{timeLeft.seconds}s</div>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <Link to={`/product/${product.slug}-p-${product._id}`}>
            <Button 
                style={{ backgroundColor: COLORS.SAGE_GREEN, borderColor: COLORS.WARM_CONCRETE }}
                className='my-3 w-50'
            >
              {endDate > currentDate && startDate < currentDate ? 'Place a bid' : 'View'}
            </Button>
          </Link>
        </Card.Body>
      </Card>
    </>
  );
}

export default Product;
